import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { take } from 'rxjs/operators';

import { ApiError, MessageService } from '../../../core';
import { GrupoClassificacao } from '../grupo-classificacao';
import { GrupoClassificacaoCrudHttpService } from '../services';
import { grupoClassificacaoSituacaoAtivo, isSituacaoInativo } from '../situacao/grupo-classificacao-situacao';
import { GrupoClassificacaoFilter } from '../filter/grupo-classificacao-filter';
import { IMultiSelectOption, IMultiSelectSettings, IMultiSelectTexts } from 'ngx-bootstrap-multiselect';

@Component({
    selector: 'sgt-grupo-classificacao-multiselect',
    templateUrl: './grupo-classificacao-multiselect.component.html',
    styleUrls: ['./grupo-classificacao-multiselect.component.less']
})
export class GrupoClassificacaoMultiselectComponent implements OnInit, OnDestroy, OnChanges {
    /** Grupos selecionados */
    @Input() idsGruposClassificacoes: number[];
    /** Ao alterar a seleçãod os grupos */
    @Output() idsGruposClassificacoesChange = new EventEmitter<number[]>();

    /** Se deve exibir a opção de selecionar a opção de grupo de classificação nulo ou seja, para filtrar registros que não possuem o grupo de classificação */
    @Input() showNullOption = false;
    /** Valor da opção de selecionar o grupo de classificação nulo */
    @Input() nullOption = false;
    /** Ao alterar a seleção da opção do grupo de classificação nulo */
    @Output() nullOptionChange = new EventEmitter<boolean>();

    /** Ao destruir o multiselect */
    @Output() onDestroy = new EventEmitter<void>();

    // Settings
    settings: IMultiSelectSettings;
    @Input() pullRight = false;
    @Input() enableSearch = true;
    @Input() showCheckAll = true;
    @Input() showUncheckAll = true;
    @Input() dynamicTitleMaxItems = 0;
    @Input() fixedTitle = false;
    @Input() selectionLimit = 0;
    @Input() autoUnselect = false;
    @Input() closeOnSelect = false;
    /** Se deve exibir o multiselect acima */
    @Input() dropup = false;
    /** Se deve desabilitar o multiselect */
    @Input() disabled = false;

    // Texts
    texts: IMultiSelectTexts;
    @Input() checkAll = 'Selecionar Todos';
    @Input() uncheckAll = 'Limpar';
    @Input() defaultTitle = 'Nenhum Grupo de Segmentos';
    @Input() checked = 'Grupo de Segmentos';
    @Input() checkedPlural = 'Grupos de Segmentos';
    @Input() searchPlaceholder = 'Pesquisar...';
    @Input() allSelected = 'Todos Selecionados';
    @Input() searchEmptyResult = 'Nenhum grupo de segmentos encontrado';
    @Input() searchNoRenderText = 'Digite para pesquisar';

    // Options
    options: IMultiSelectOption[];

    /** Se deve filtrar somente os grupos ativos */
    @Input() somenteAtivos = false;
    /** Se deve considerar Análise de Mercado */
    @Input() considerarClienteAnaliseMercado: boolean = null;
    /** Se deve considerar Meta */
    @Input() considerarMeta: boolean = null;

    constructor(private messageService: MessageService,
                private grupoClassificacaoCrudHttpService: GrupoClassificacaoCrudHttpService,
                private upperCasePipe: UpperCasePipe) { }

    ngOnInit() {
        // Settings
        this.createSettings();
        // Texts
        this.createTexts();

        this.create();
    }

    ngOnChanges(changes: SimpleChanges) {
        for (const propName in changes) {
            if (propName === 'selectionLimit' && !changes[propName].isFirstChange()) {
                this.createSettings();
            } else if (propName === 'autoUnselect' && !changes[propName].isFirstChange()) {
                this.createSettings();
            } else if (propName === 'closeOnSelect' && !changes[propName].isFirstChange()) {
                this.createSettings();
            }
        }
    }

    /** Cria os settings do multiselect */
    private createSettings() {
        this.settings = {
            pullRight: this.pullRight,
            enableSearch: this.enableSearch,
            containerClasses: `container-multiselect container-multiselect-grupo-classificacao ${this.dropup ? 'dropup' : 'dropdown'}`,
            showCheckAll: this.showCheckAll,
            showUncheckAll: this.showUncheckAll,
            dynamicTitleMaxItems: this.dynamicTitleMaxItems,
            fixedTitle: this.fixedTitle,
            selectionLimit: this.selectionLimit,
            autoUnselect: this.autoUnselect,
            closeOnSelect: this.closeOnSelect
        };
    }

    /** Cria os texts do multiselect */
    private createTexts() {
        this.texts = {
            checkAll: this.checkAll,
            uncheckAll: this.uncheckAll,
            defaultTitle: this.defaultTitle,
            checked: this.checked,
            checkedPlural: this.checkedPlural,
            searchPlaceholder: this.searchPlaceholder,
            allSelected: this.allSelected,
            searchEmptyResult: this.searchEmptyResult,
            searchNoRenderText: this.searchNoRenderText
        };
    }

    /** Cria o filtro da pesquisa dos grupos de classificações com as opções do multselect */
    private createFilter(): GrupoClassificacaoFilter {
        const filter = new GrupoClassificacaoFilter();
        // Somente ativas
        if (this.somenteAtivos) {
            filter.situacao = grupoClassificacaoSituacaoAtivo.id;
        }
        // Considerar Cliente Análise Mercado
        if (this.considerarClienteAnaliseMercado != null && this.considerarClienteAnaliseMercado !== undefined) {
            filter.considerarClienteAnaliseMercado = this.considerarClienteAnaliseMercado;
        }
        // Considerar Meta
        if (this.considerarMeta != null && this.considerarMeta !== undefined) {
            filter.considerarMeta = this.considerarMeta;
        }

        return filter;
    }

    /** Pesquisa os grupos de classificações */
    private search(): Promise<GrupoClassificacao[]> {
        return new Promise<GrupoClassificacao[]>((resolve, reject) => {
            const filter = this.createFilter();
            this.grupoClassificacaoCrudHttpService.searchWithFilter(filter)
            .pipe(
                take(1)
            )
            .subscribe(gruposClassificacoes => resolve(gruposClassificacoes),
                (error: ApiError) => {
                    this.messageService.error(error.message,  'Erro ao pesquisar os grupos de segmentos', error);
                    reject();
                }
            );
        });
    }

    /** Cria os grupos de classificações para o multiselect */
    private async create() {
        this.options = [];
        const selects: IMultiSelectOption[] = [];

        // Adicionando os grupos e suas classificações ao multiselect
        const gruposClassificacoes = await this.search();
        if (gruposClassificacoes) {
            // Adicionando a opção de "Sem Grupo de Segmentos"
            if (this.showNullOption) {
                selects.push({
                    id: 0,
                    name: 'SEM GRUPO DE SEGMENTOS'
                });
            }

            gruposClassificacoes?.forEach(grupoClassificacao => {
                selects.push({
                    id: grupoClassificacao.id,
                    name: isSituacaoInativo(grupoClassificacao.situacao) ? `${grupoClassificacao.nome} (INATIVO)` : grupoClassificacao.nome
                });
            });

            this.options = selects;
        }
    }


    /** Ao alterar a seleção da opção de grupo de classificação nulo */
    private onChangeNullOption() {
        this.nullOption = this.idsGruposClassificacoes.includes(0);
        this.nullOptionChange.emit(this.nullOption);

    }

    /** Ao alterar a seleção dos grupos de classificações */
    onChange() {
        this.onChangeNullOption();
        this.idsGruposClassificacoesChange.emit(this.idsGruposClassificacoes);
    }

    ngOnDestroy() {
        this.onDestroy.emit();
    }
}
